import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query"
import { ThemeRoot, ToastProvider } from "@ternary/ui"
import { type ReactNode, useState } from "react"
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom"
import { loadAdminSession } from "./api.js"
import { AdminShell } from "./components/admin-shell.js"
import { adminQueryClientConfig } from "./query-client.js"
import { AdminAccountPage } from "./routes/admin-account-page.js"
import { AdminAddressPage } from "./routes/admin-address-page.js"
import { AdminConnectPage } from "./routes/admin-connect-page.js"
import { AdminCouponsPage } from "./routes/admin-coupons-page.js"
import { AdminCredentialPage } from "./routes/admin-credential-page.js"
import { AdminCustomerPage } from "./routes/admin-customer-page.js"
import { AdminCustomersPage } from "./routes/admin-customers-page.js"
import { AdminDashboardPage } from "./routes/admin-dashboard-page.js"
import { AdminDeveloperPage } from "./routes/admin-developer-page.js"
import { AdminDiscordPage } from "./routes/admin-discord-page.js"
import { AdminDomainsPage } from "./routes/admin-domains-page.js"
import { AdminLoginPage } from "./routes/admin-login-page.js"
import { AdminOnboardingPage } from "./routes/admin-onboarding-page.js"
import { AdminPaymentGatewayPage } from "./routes/admin-payment-gateway-page.js"
import { AdminPixelsPage } from "./routes/admin-pixels-page.js"
import { AdminPollsPage } from "./routes/admin-polls-page.js"
import { AdminProductWizardPage } from "./routes/admin-product-wizard-page.js"
import { AdminProductsPage } from "./routes/admin-products-page.js"
import { AdminRegisterConfirmPage, AdminRegisterStartPage } from "./routes/admin-register-page.js"
import { AdminRewardsPage } from "./routes/admin-rewards-page.js"
import { AdminSavedPostsPage } from "./routes/admin-saved-posts-page.js"
import { AdminSettingsPage } from "./routes/admin-settings-page.js"
import { AdminTeamPage } from "./routes/admin-team-page.js"
import { AdminUserProfilePage, AdminUsersPage } from "./routes/admin-users-page.js"

function ProtectedAdmin() {
  const session = useQuery({
    queryFn: loadAdminSession,
    queryKey: ["admin-session"],
  })

  if (session.isPending) {
    return (
      <main aria-busy="true" className="admin-loading">
        <span aria-hidden="true" className="tny-spinner" />
        <p>Loading dashboard…</p>
      </main>
    )
  }

  if (session.isError || !session.data.authenticated) {
    return <Navigate replace to="/" />
  }

  return (
    <AdminShell session={session.data.session}>
      <Outlet />
    </AdminShell>
  )
}

/**
 * The admin root, shared with the Storybook admin stories: the default Ternary theme (the admin
 * carries no tenant brand), following the light or dark colour scheme, and the toasts.
 */
export function AdminRoot({ children }: { children: ReactNode }) {
  return (
    <ThemeRoot>
      <ToastProvider>{children}</ToastProvider>
    </ThemeRoot>
  )
}

export function AdminApp() {
  const [queryClient] = useState(() => new QueryClient(adminQueryClientConfig))

  return (
    <QueryClientProvider client={queryClient}>
      <AdminRoot>
        <BrowserRouter>
          <Routes>
            <Route element={<AdminLoginPage />} path="/" />
            <Route element={<AdminLoginPage />} path="login" />
            <Route element={<AdminCredentialPage purpose="reset" />} path="reset/user/:token" />
            <Route element={<AdminCredentialPage purpose="create" />} path="create/:token" />
            <Route element={<AdminRegisterStartPage />} path="register" />
            <Route element={<AdminRegisterConfirmPage />} path="register/confirm" />
            <Route element={<AdminOnboardingPage />} path="onboarding/:token" />
            {/* Claude's sign-in (the read-only MCP): reachable signed out, it signs in first. */}
            <Route element={<AdminConnectPage />} path="connect" />
            <Route element={<ProtectedAdmin />}>
              <Route element={<AdminDashboardPage />} path="dashboard" />
              <Route element={<AdminCustomersPage />} path="dashboard/customers" />
              <Route element={<AdminCustomerPage />} path="dashboard/customers/:id" />
              <Route element={<AdminDiscordPage />} path="dashboard/discord" />
              <Route element={<AdminPollsPage />} path="dashboard/discord/polls" />
              <Route element={<AdminSavedPostsPage />} path="dashboard/discord/saved-posts" />
              <Route element={<AdminPixelsPage />} path="dashboard/pixels" />
              <Route element={<AdminPaymentGatewayPage />} path="dashboard/stripe" />
              <Route element={<AdminSettingsPage />} path="dashboard/settings" />
              <Route
                element={<Navigate replace to="/dashboard/settings/domains" />}
                path="dashboard/settings/domain"
              />
              <Route element={<AdminDomainsPage />} path="dashboard/settings/domains" />
              <Route element={<AdminAddressPage />} path="dashboard/settings/address" />
              <Route
                element={<AdminSettingsPage active="email" />}
                path="dashboard/settings/email"
              />
              <Route element={<AdminDeveloperPage />} path="dashboard/settings/developer" />
              <Route element={<AdminTeamPage />} path="dashboard/team" />
              <Route
                element={<Navigate replace to="/dashboard/team" />}
                path="dashboard/settings/team"
              />
              <Route element={<AdminProductsPage />} path="dashboard/products" />
              <Route element={<AdminProductWizardPage />} path="dashboard/products/new" />
              <Route element={<AdminProductWizardPage />} path="dashboard/products/:id/edit" />
              <Route element={<AdminRewardsPage />} path="dashboard/rewards" />
              <Route element={<AdminCouponsPage />} path="dashboard/coupons" />
              <Route element={<AdminUsersPage />} path="dashboard/users" />
              <Route element={<AdminUserProfilePage />} path="dashboard/users/:id" />
              <Route element={<AdminAccountPage />} path="dashboard/account" />
            </Route>
            <Route element={<Navigate replace to="/" />} path="*" />
          </Routes>
        </BrowserRouter>
      </AdminRoot>
    </QueryClientProvider>
  )
}
